import {VaultedPaymentMethod} from '@primer-io/checkout-web';
import {createAction, createReducer} from '@reduxjs/toolkit';

import {AxiosError} from 'axios';

import {isNetworkError, NETWORK_ERROR_MESSAGE} from 'libs/retryError/retryError';
import {CompleteOrderType} from 'types/completeOrderType';

import {completeOrderReview} from '../../actions/completeOrder/completeOrder';
import {setCheckoutStep} from '../currentStep/currentStep';
import {changePaymentMethod} from '../paymentStep/paymentMethods/paymentMethods';

export const initialState = {
    analyticsMessage: '',
    isApplePayAvailable: false,
    isCreditCardLoading: false,
    isSaveCardChecked: false,
    uiMessage: '',
    vaultedPaymentMethods: [] as VaultedPaymentMethod[],
    vaultPaymentId: '',
};

export type PrimerStateType = typeof initialState;

export const setSaveCardChecked = createAction<boolean>('CHECKOUT@SET_SAVE_CARD_CHECKED');
export const setIsCreditCardLoading = createAction<boolean>(
    'CHECKOUT@SET_IS_PRIMER_CREDIT_CARD_LOADING',
);
export const setVaultPaymentId = createAction<string>('CHECKOUT@SET_VAULT_PAYMENT_ID');
export const setVaultedPaymentMethods = createAction<VaultedPaymentMethod[]>(
    'CHECKOUT@SET_VAULTED_PAYMENT_METHODS',
);

export const setAnalyticsMessage = createAction<string>('CHECKOUT@SET_ANALYTICS_MESSAGE');
export const setUiMessage = createAction<string>('CHECKOUT@SET_UI_MESSAGE');
export const setIsApplePayAvailable = createAction<boolean>('CHECKOUT@SET_IS_APPLE_PAY_AVAILABLE');

const saveOrderErrorResponseAdapter = (payload?: AxiosError<CompleteOrderType>) => {
    const defaultMessage = isNetworkError(payload)
        ? NETWORK_ERROR_MESSAGE
        : 'Something went wrong, please try another payment method or try later';
    const actualErrorMessage =
        payload?.response?.data?.error_friendly_message ||
        payload?.response?.data?.error ||
        defaultMessage;

    return String(actualErrorMessage);
};

export const primer = createReducer(initialState, builder => {
    builder
        // reset state when switching between primer_cc and primer_cc_fsa
        .addCase(changePaymentMethod, state => ({
            ...state,
            analyticsMessage: '',
            isCreditCardLoading: false,
            isSaveCardChecked: false,
            uiMessage: '',
        }))
        .addCase(setSaveCardChecked, (state, action) => ({
            ...state,
            isSaveCardChecked: action.payload,
        }))
        .addCase(setVaultPaymentId, (state, action) => ({
            ...state,
            vaultPaymentId: action.payload,
        }))
        .addCase(setVaultedPaymentMethods, (state, action) => ({
            ...state,
            vaultedPaymentMethods: action.payload,
        }))
        .addCase(setIsCreditCardLoading, (state, action) => ({
            ...state,
            isCreditCardLoading: action.payload,
        }))
        .addCase(setAnalyticsMessage, (state, action) => ({
            ...state,
            analyticsMessage: action.payload,
        }))
        .addCase(setUiMessage, (state, action) => ({
            ...state,
            uiMessage: action.payload,
        }))
        .addCase(completeOrderReview.rejected, (state, action) => ({
            ...state,
            analyticsMessage: saveOrderErrorResponseAdapter(action.payload),
            uiMessage: saveOrderErrorResponseAdapter(action.payload),
        }))
        .addCase(setCheckoutStep, state => ({
            ...state,
            analyticsMessage: '',
            uiMessage: '',
        }))
        .addCase(setIsApplePayAvailable, (state, action) => ({
            ...state,
            isApplePayAvailable: action.payload,
        }));
});
